'use client'

import type { User } from '@/payload-types'

import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react'
import { useRouter } from 'next/navigation'
import posthog from 'posthog-js'

// eslint-disable-next-line no-unused-vars
type ResetPassword = (args: {
  password: string
  passwordConfirm: string
  token: string
}) => Promise<void>

type ForgotPassword = (args: { email: string }) => Promise<void> // eslint-disable-line no-unused-vars

type Create = (args: { email: string; password: string; passwordConfirm: string }) => Promise<void> // eslint-disable-line no-unused-vars

type Login = (args: { email: string; password: string }) => Promise<User> // eslint-disable-line no-unused-vars

type Logout = () => Promise<void>

type AuthContext = {
  create: Create
  forgotPassword: ForgotPassword
  login: Login
  logout: Logout
  refreshUser: () => Promise<void>
  resetPassword: ResetPassword
  setUser: (user: User | null) => void // eslint-disable-line no-unused-vars
  status: 'loggedIn' | 'loggedOut' | undefined
  user?: User | null
}

const Context = createContext({} as AuthContext)

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const router = useRouter()
  const [user, setUserState] = useState<User | null>()

  // used to track the single event of logging in or logging out
  // useful for `useEffect` hooks that should only run once
  const [status, setStatus] = useState<'loggedIn' | 'loggedOut' | undefined>()

  // Prevent concurrent refreshUser calls from racing
  const refreshPromiseRef = useRef<Promise<void> | null>(null)

  // Wrapper that also updates status when user is set externally (e.g., from AdminBar)
  const setUser = useCallback((newUser: User | null) => {
    setUserState(newUser)
    setStatus(newUser ? 'loggedIn' : 'loggedOut')

    if (newUser) {
      posthog.identify(String(newUser.id), { email: newUser.email })
    } else if (posthog._isIdentified()) {
      // Only reset identified users — resetting an anonymous visitor would
      // mint a new anonymous ID on every failed session refresh
      posthog.reset()
    }
  }, [])

  const refreshUser = useCallback(async () => {
    // Deduplicate: if a refresh is already in-flight, reuse it
    if (refreshPromiseRef.current) {
      return refreshPromiseRef.current
    }

    const promise = (async () => {
      try {
        const res = await fetch(`/api/users/me?depth=1`, {
          cache: 'no-store',
          credentials: 'include',
          headers: {
            'Content-Type': 'application/json',
          },
          method: 'GET',
        })

        if (res.status === 401 || res.status === 403) {
          setUser(null)
          return
        }

        if (!res.ok) {
          // Network/server error — don't clear the user to avoid false logouts on iOS
          return
        }

        const { user: meUser } = await res.json()
        setUser(meUser || null)
      } catch (_) {
        // Network error — don't clear the user to avoid false logouts on iOS
      } finally {
        refreshPromiseRef.current = null
      }
    })()

    refreshPromiseRef.current = promise
    return promise
  }, [setUser])

  const create = useCallback<Create>(async (args) => {
    try {
      const res = await fetch(`/api/users/create`, {
        body: JSON.stringify({
          email: args.email,
          password: args.password,
          passwordConfirm: args.passwordConfirm,
        }),
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
        method: 'POST',
      })

      if (res.ok) {
        const { data, errors } = await res.json()
        if (errors) throw new Error(errors[0].message)
        setUser(data?.loginUser?.user)
        router.refresh()
      } else {
        throw new Error('Invalid login')
      }
    } catch (e) {
      throw new Error('An error occurred while attempting to login.')
    }
  }, [router, setUser])

  const login = useCallback<Login>(async (args) => {
    try {
      const res = await fetch(`/api/users/login`, {
        body: JSON.stringify({
          email: args.email,
          password: args.password,
        }),
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
        method: 'POST',
      })

      if (res.ok) {
        const { errors, user: loggedInUser } = await res.json()
        if (errors) throw new Error(errors[0].message)
        setUser(loggedInUser)
        router.refresh()
        return loggedInUser
      }

      throw new Error('Invalid login')
    } catch (e) {
      throw new Error('An error occurred while attempting to login.')
    }
  }, [router, setUser])

  const logout = useCallback<Logout>(async () => {
    try {
      const res = await fetch(`/api/logout`, {
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
        method: 'POST',
      })

      if (res.ok) {
        setUser(null)
      } else {
        const data = await res.json().catch(() => null)
        throw new Error(data?.message || 'An error occurred while attempting to logout.')
      }
    } catch (e) {
      throw e instanceof Error
        ? e
        : new Error('An error occurred while attempting to logout.')
    }
  }, [router, setUser])

  // Stable ref so event handlers always call the latest refreshUser without re-registering
  const refreshUserRef = useRef(refreshUser)
  refreshUserRef.current = refreshUser

  useEffect(() => {
    // Initial fetch — runs once
    void refreshUserRef.current()

    // Debounce visibility events to avoid rapid-fire calls on mobile
    let debounceTimer: ReturnType<typeof setTimeout> | null = null

    const debouncedRefresh = () => {
      if (debounceTimer) clearTimeout(debounceTimer)
      debounceTimer = setTimeout(() => {
        void refreshUserRef.current()
      }, 500)
    }

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        debouncedRefresh()
      }
    }

    document.addEventListener('visibilitychange', handleVisibilityChange)

    return () => {
      if (debounceTimer) clearTimeout(debounceTimer)
      document.removeEventListener('visibilitychange', handleVisibilityChange)
    }
  }, []) // empty deps — runs once, uses refs for stable access

  const forgotPassword = useCallback<ForgotPassword>(async (args) => {
    try {
      const res = await fetch(`/api/users/forgot-password`, {
        body: JSON.stringify({
          email: args.email,
        }),
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
        method: 'POST',
      })

      if (res.ok) {
        const { data, errors } = await res.json()
        if (errors) throw new Error(errors[0].message)
        setUser(data?.loginUser?.user)
        router.refresh()
      } else {
        throw new Error('Invalid login')
      }
    } catch (e) {
      throw new Error('An error occurred while attempting to login.')
    }
  }, [router, setUser])

  const resetPassword = useCallback<ResetPassword>(async (args) => {
    try {
      const res = await fetch(`/api/users/reset-password`, {
        body: JSON.stringify({
          password: args.password,
          passwordConfirm: args.passwordConfirm,
          token: args.token,
        }),
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
        method: 'POST',
      })

      if (res.ok) {
        const { data, errors } = await res.json()
        if (errors) throw new Error(errors[0].message)
        setUser(data?.loginUser?.user)
        router.refresh()
      } else {
        throw new Error('Invalid login')
      }
    } catch (e) {
      throw new Error('An error occurred while attempting to login.')
    }
  }, [router, setUser])

  return (
    <Context.Provider
      value={{
        create,
        forgotPassword,
        login,
        logout,
        refreshUser,
        resetPassword,
        setUser,
        status,
        user,
      }}
    >
      {children}
    </Context.Provider>
  )
}

type UseAuth<T = User> = () => AuthContext // eslint-disable-line no-unused-vars

export const useAuth: UseAuth = () => useContext(Context)
